import React, { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle2, ChevronRight } from "lucide-react";
import { BRAND, SERVICES } from "@/lib/site";
import { SERVICE_DETAILS } from "@/lib/service-details";
import { CITIES } from "@/lib/locations";
import ServiceIcon from "@/components/site/ServiceIcon";
import ContactForm from "@/components/site/ContactForm";
import Seo from "@/components/site/Seo";

export default function ServiceDetail() {
  const { serviceSlug } = useParams();
  const service = SERVICES.find((s) => s.slug === serviceSlug);
  const detail = SERVICE_DETAILS[serviceSlug];

  const path = service ? `/services/${service.slug}` : "/services";
  const seoTitle = service ? `${service.title} — In-Home Care in Southwest Florida` : "";
  const seoDesc = service && detail
    ? `${service.title} for seniors across Naples, Fort Myers, Cape Coral & Bonita Springs. ${detail.lead} Free consultation with Hortense's team.`
    : "";

  const jsonLd = useMemo(
    () =>
      service && detail
        ? {
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": service.title,
            "provider": {
              "@type": "LocalBusiness",
              "name": BRAND.fullName,
              "telephone": "+1-239-834-4377",
            },
            "areaServed": CITIES.map((c) => ({
              "@type": "City",
              "name": c.name,
              "containedInPlace": { "@type": "State", "name": "Florida" },
            })),
            "url": `https://yourelitehomecare.com${path}`,
            "name": `${service.title} in Southwest Florida`,
            "description": detail.lead,
            "mainEntityOfPage": {
              "@type": "FAQPage",
              "mainEntity": detail.faqs.map((f) => ({
                "@type": "Question",
                "name": f.q,
                "acceptedAnswer": { "@type": "Answer", "text": f.a },
              })),
            },
          }
        : null,
    [service, detail, path]
  );

  if (!service || !detail) return <Navigate to="/services" replace />;

  const otherServices = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 3);

  return (
    <div data-testid={`page-service-${service.slug}`}>
      <Seo path={path} title={seoTitle} description={seoDesc} jsonLd={jsonLd} />

      {/* HEADER */}
      <section className="relative bg-navy-900 text-white overflow-hidden isolate">
        <div
          aria-hidden
          className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-gold-400/15 blur-3xl"
        />
        <div className="container-x pt-20 pb-24 md:pt-24 md:pb-28">
          <nav className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold-300/90">
            <Link to="/services" data-testid="service-breadcrumb-services" className="hover:text-gold-200">
              Services
            </Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-white/60">{service.title}</span>
          </nav>
          <div className="mt-6 flex items-start gap-5">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 border border-white/20 text-gold-300">
              <ServiceIcon name={service.icon} className="h-7 w-7" />
            </span>
            <div>
              <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] text-white max-w-3xl">
                {detail.hero}
              </h1>
              <p className="mt-6 text-white/85 text-lg max-w-2xl leading-relaxed">
                {detail.lead}
              </p>
            </div>
          </div>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid={`service-${service.slug}-cta-consultation`}
              className="btn-primary"
            >
              Schedule a Free Consultation <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={BRAND.phoneHref}
              data-testid={`service-${service.slug}-cta-call`}
              className="btn-ghost-light"
            >
              <Phone className="h-4 w-4" strokeWidth={1.75} /> {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-14">
          <div>
            <span className="overline">What's included</span>
            <h2 className="text-4xl md:text-5xl leading-tight">
              Every detail,{" "}
              <span className="italic text-gold-500">thoughtfully handled.</span>
            </h2>
            <ul className="mt-8 space-y-4">
              {detail.includes.map((line) => (
                <li key={line} className="flex items-start gap-3 text-navy-800">
                  <CheckCircle2
                    className="h-5 w-5 text-gold-500 mt-0.5 shrink-0"
                    strokeWidth={1.6}
                  />
                  <span className="text-lg leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="card-soft p-8 md:p-10 bg-navy-50/60 h-fit">
            <div className="text-xs uppercase tracking-[0.2em] text-gold-500">
              Ideal for
            </div>
            <p className="mt-3 text-navy-800 leading-relaxed">
              {detail.idealFor}
            </p>
            <div className="mt-8 pt-8 border-t border-navy-900/10">
              <div className="text-xs uppercase tracking-[0.2em] text-navy-500">
                Serving
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {CITIES.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/home-care/${c.slug}`}
                    data-testid={`service-${service.slug}-city-${c.slug}`}
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-white border border-navy-900/10 hover:border-gold-400 hover:text-gold-600 transition-colors text-navy-800"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-navy-50/50 border-y border-navy-900/5">
        <div className="container-x max-w-4xl">
          <span className="overline">Frequently asked</span>
          <h2 className="text-4xl md:text-5xl leading-tight">Good questions, honest answers.</h2>
          <div className="mt-10 space-y-4">
            {detail.faqs.map((f, i) => (
              <details
                key={f.q}
                data-testid={`service-${service.slug}-faq-${i}`}
                className="card-soft p-6 md:p-8 group [&_summary]:cursor-pointer"
              >
                <summary className="flex items-start justify-between gap-4 list-none">
                  <span className="font-serif text-xl text-navy-900">{f.q}</span>
                  <ChevronRight className="h-5 w-5 text-gold-500 mt-1 transition-transform group-open:rotate-90 shrink-0" />
                </summary>
                <p className="mt-4 text-navy-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* OTHER SERVICES + CTA */}
      <section className="section">
        <div className="container-x">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <span className="overline">Explore more</span>
              <h2 className="text-3xl md:text-4xl leading-tight">
                Other ways we can help.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid={`service-${service.slug}-view-all`}
              className="inline-flex items-center gap-2 text-navy-900 font-medium hover:text-gold-500 transition-colors"
            >
              View all services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {otherServices.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`service-${service.slug}-related-${s.slug}`}
                className="card-soft p-8 group"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-navy-900 text-gold-300 group-hover:bg-gold-400 group-hover:text-navy-900 transition-colors">
                  <ServiceIcon name={s.icon} className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-serif text-xl text-navy-900">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-navy-600 leading-relaxed">
                  {s.short}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="section bg-navy-50/40 border-t border-navy-900/5">
        <div className="container-x grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <span className="overline">Start with a free consultation</span>
            <h2 className="text-4xl md:text-5xl leading-tight">
              Let's build a<br />
              <span className="italic text-gold-500">care plan.</span>
            </h2>
            <p className="mt-5 text-navy-600 text-lg leading-relaxed">
              A 20-minute call with our care coordinator — no pressure, no
              obligation.
            </p>
            <a
              href={BRAND.phoneHref}
              data-testid={`service-${service.slug}-phone-inline`}
              className="inline-flex items-center gap-3 mt-6 text-navy-900 hover:text-gold-500 transition-colors"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-navy-900 text-gold-300">
                <Phone className="h-5 w-5" strokeWidth={1.6} />
              </span>
              <span>
                <span className="block text-sm text-navy-500">Call us</span>
                <span className="font-serif text-2xl">{BRAND.phone}</span>
              </span>
            </a>
          </div>
          <div className="lg:col-span-3">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
